import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Phone, X, Star, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface StickyPremiumCTAProps {
  listing: any;
  onContactClick: () => void;
  displayData: any;
}

export const StickyPremiumCTA = ({ listing, onContactClick, displayData }: StickyPremiumCTAProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling 300px
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (isDismissed || !isVisible) return null;

  return (
    <>
      {/* Mobile Sticky Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t-2 border-primary/20 shadow-2xl p-4 animate-slide-up">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="font-bold text-sm mb-1">{listing.name}</p>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-semibold">{listing.rating || 4.8}</span>
              <span className="text-xs text-muted-foreground">({listing.review_count || 127})</span>
            </div>
          </div>
          
          <Button 
            className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white shadow-lg px-6"
            onClick={onContactClick}
          >
            <Phone className="w-4 h-4 mr-2" />
            Contactar
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon"
            className="flex-shrink-0"
            onClick={() => setIsDismissed(true)}
            aria-label="Cerrar barra de contacto"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Desktop Floating Action Button */}
      <div className="hidden lg:block fixed bottom-8 right-8 z-50 animate-bounce-gentle">
        <div className="relative">
          {/* Pulse Ring */}
          <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
          
          {/* Main Button */}
          <Button 
            size="lg"
            className="relative bg-gradient-to-r from-primary via-primary/90 to-primary/80 hover:from-primary/95 hover:to-primary/85 text-white shadow-2xl px-8 py-6 text-lg font-bold rounded-full border-2 border-white/20"
            onClick={onContactClick}
          >
            <Phone className="w-6 h-6 mr-2" />
            {displayData.cta_text || "Contactar Ahora"}
          </Button>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <Button
        size="icon"
        variant="outline"
        className="hidden lg:flex fixed bottom-8 left-8 z-50 bg-background/95 backdrop-blur-lg shadow-xl hover:shadow-2xl transition-all"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Volver arriba"
      >
        <ArrowUp className="w-5 h-5" aria-hidden="true" />
      </Button>
    </>
  );
};
